import { formatDate, parseDate } from '../dateString.js';
import { allAmounts, allPayments, amountOf, cappedAmount, chargeTypeAmount, constDate, constNum, context, daysLate, describeAmount, giveAmount, isLate, multiply, once, repeat, scaleAmount, unit, whenAmount, } from './constructors.js';
import { recurrenceFor } from './dsl.js';
export function firstDueDate(startDate, dueDay) {
    if (!Number.isInteger(dueDay) || dueDay < 1 || dueDay > 31) {
        throw new RangeError(`A due day must be a whole number from 1 to 31, not ${dueDay}`);
    }
    const start = parseDate(startDate);
    let month = new Date(start.getFullYear(), start.getMonth(), 1);
    if (start.getDate() > dueDay)
        month = new Date(month.getFullYear(), month.getMonth() + 1, 1);
    for (;;) {
        const due = new Date(month.getFullYear(), month.getMonth(), dueDay);
        if (due.getMonth() === month.getMonth())
            return formatDate(due);
        month = new Date(month.getFullYear(), month.getMonth() + 1, 1);
    }
}
const MONTH_LENGTHS = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
export function firstAnnualDate(startDate, month, day) {
    if (!Number.isInteger(month) || month < 1 || month > 12) {
        throw new RangeError(`A month must be a whole number from 1 to 12, not ${month}`);
    }
    if (!Number.isInteger(day) || day < 1 || day > MONTH_LENGTHS[month - 1]) {
        throw new RangeError(`Month ${month} has no day ${day}`);
    }
    const start = parseDate(startDate);
    for (let year = start.getFullYear();; year++) {
        const due = new Date(year, month - 1, day);
        if (due.getMonth() === month - 1 && due >= start)
            return formatDate(due);
    }
}
function monthlyOnDueDay(dueDay, amount, currency, startDate, endDate) {
    const start = firstDueDate(startDate, dueDay);
    return repeat(recurrenceFor('monthly', 1, start), amount, currency, start, endDate);
}
export function monthlyPayment(amount, dayOfMonthValue, startDate, endDate, currency = 'USD') {
    return monthlyOnDueDay(dayOfMonthValue, scaleAmount(amount, unit()), currency, startDate, endDate);
}
export function flatDailyLateFee(dailyFee, gracePeriod = 0) {
    return whenAmount(isLate(gracePeriod), amountOf(multiply(constNum(dailyFee), daysLate(gracePeriod))));
}
export function percentageLateFee(percentage, baseAmount, gracePeriod = 0) {
    const fee = baseAmount * percentage;
    return whenAmount(isLate(gracePeriod), scaleAmount(fee, unit()));
}
export function percentageLateFeeOn(percentage, balance, gracePeriod = 0) {
    return whenAmount(isLate(gracePeriod), scaleAmount(percentage, amountOf(balance)));
}
export function cappedDailyLateFee(dailyFee, maxFee, gracePeriod = 0) {
    return cappedAmount(flatDailyLateFee(dailyFee, gracePeriod), maxFee);
}
export function monthlyRentWithLateFee(rentAmount, dueDay, dailyLateFee, gracePeriod, startDate, endDate, currency = 'USD') {
    const amount = allAmounts(describeAmount('Monthly Rent', scaleAmount(rentAmount, unit())), describeAmount('Late Fee', flatDailyLateFee(dailyLateFee, gracePeriod)));
    return monthlyOnDueDay(dueDay, amount, currency, startDate, endDate);
}
export function monthlyRentWithCappedLateFee(rentAmount, dueDay, dailyLateFee, maxLateFee, gracePeriod, startDate, endDate, currency = 'USD') {
    const amount = allAmounts(describeAmount('Monthly Rent', scaleAmount(rentAmount, unit())), describeAmount('Late Fee', cappedDailyLateFee(dailyLateFee, maxLateFee, gracePeriod)));
    return monthlyOnDueDay(dueDay, amount, currency, startDate, endDate);
}
export function securityDeposit(amount, dueDate, returnDate, currency = 'USD') {
    if (returnDate < dueDate) {
        throw new Error('Return date must be after due date');
    }
    const deposit = once(constDate(dueDate), describeAmount('Security Deposit', scaleAmount(amount, unit())), currency);
    const refund = once(constDate(returnDate), describeAmount('Security Deposit Refund', whenAmount(context('contractTerminated'), giveAmount(scaleAmount(amount, unit())))), currency);
    return allPayments(deposit, refund);
}
export function proratedRent(fullAmount, daysInMonth, daysOccupied, dueDate, currency = 'USD') {
    const proratedAmount = (fullAmount / daysInMonth) * daysOccupied;
    return once(constDate(dueDate), describeAmount('Prorated Rent', scaleAmount(proratedAmount, unit())), currency);
}
export function annualPayment(amount, month, day, startDate, endDate, currency = 'USD') {
    const start = firstAnnualDate(startDate, month, day);
    return repeat(recurrenceFor('yearly', 1, start), scaleAmount(amount, unit()), currency, start, endDate);
}
export function variableUtilityPayment(dueDay, startDate, endDate, currency = 'USD') {
    return monthlyOnDueDay(dueDay, describeAmount('Utility Payment', unit()), currency, startDate, endDate);
}
export function residentialLeaseSchedule(params) {
    const currency = params.currency ?? 'USD';
    const payments = [];
    payments.push(securityDeposit(params.securityDepositAmount, params.leaseStartDate, params.leaseEndDate, currency));
    if (params.proratedDays) {
        payments.push(proratedRent(params.rentAmount, params.proratedDays.daysInMonth, params.proratedDays.daysOccupied, params.leaseStartDate, currency));
    }
    payments.push(monthlyRentWithLateFee(params.rentAmount, params.rentDueDay, params.dailyLateFee, params.gracePeriod, params.leaseStartDate, params.leaseEndDate, currency));
    return allPayments(...payments);
}
export function hoaBillingSchedule(assessment, dayOfMonthValue, startDate, gracePeriod, currency = 'USD') {
    const recurringAmounts = [];
    if (assessment.commonCharge && assessment.commonCharge > 0) {
        recurringAmounts.push(chargeTypeAmount('common-charge', 'Monthly Common Charge', scaleAmount(assessment.commonCharge, unit())));
    }
    const specialAssessments = [
        ['Special Assessment 1', assessment.specialAssessment1],
        ['Special Assessment 2', assessment.specialAssessment2],
        ['Special Assessment 3', assessment.specialAssessment3],
        ['Special Assessment 4', assessment.specialAssessment4],
    ];
    for (const [name, amount] of specialAssessments) {
        if (amount && amount > 0) {
            recurringAmounts.push(chargeTypeAmount('special-assessment', name, scaleAmount(amount, unit())));
        }
    }
    if (assessment.parking && assessment.parking > 0) {
        recurringAmounts.push(chargeTypeAmount('parking', 'Parking', scaleAmount(assessment.parking, unit())));
    }
    if (assessment.others && assessment.others > 0) {
        recurringAmounts.push(chargeTypeAmount('other-charge', 'Other Charges', scaleAmount(assessment.others, unit())));
    }
    if (assessment.finesOrAdjustments && assessment.finesOrAdjustments !== 0) {
        recurringAmounts.push(chargeTypeAmount('fines-adjustments', 'Fines/Adjustments', scaleAmount(assessment.finesOrAdjustments, unit())));
    }
    if (assessment.commonChargeLate && assessment.commonChargeLate > 0) {
        recurringAmounts.push(chargeTypeAmount('late-fee', 'Late Fee', flatDailyLateFee(assessment.commonChargeLate, gracePeriod)));
    }
    if (recurringAmounts.length === 0) {
        recurringAmounts.push(scaleAmount(0, unit()));
    }
    const recurringPayment = monthlyOnDueDay(dayOfMonthValue, allAmounts(...recurringAmounts), currency, startDate);
    if (assessment.currentBalance && assessment.currentBalance !== 0) {
        const openingBalance = once(constDate(startDate), chargeTypeAmount('opening-balance', 'Opening Balance', scaleAmount(assessment.currentBalance, unit())), currency);
        return allPayments(openingBalance, recurringPayment);
    }
    return recurringPayment;
}
//# sourceMappingURL=recipes.js.map